import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  IoCall,
  IoCheckmarkCircle,
  IoChevronDown,
  IoClose,
  IoLocationSharp,
  IoLockClosed,
  IoMail,
  IoPerson,
  IoShield,
  IoTime,
  IoEye,
  IoEyeOff,
  IoBusiness,
} from "react-icons/io5";
import { MdEdit, MdSave, MdCancel } from "react-icons/md";
import Card from "components/card";
import { Country, State, City } from "country-state-city";
import { getToken } from "utils/auth";
import { API_BASE_URL } from "utils/api";
import { useUser } from "contexts/UserContext";

// ─── API ──────────────────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // needed if backend uses cookies/sessions
  headers: {
    "ngrok-skip-browser-warning": "true",
    "Content-Type": "application/json",
  },
});
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const profileApi = {
  get: async () => {
    const res = await api.get("/users/auth/me");
    return res.data?.data || res.data;
  },
  update: async (payload) => {
    const hasFile = Object.values(payload).some((v) => v instanceof File);
    let body = payload;
    if (hasFile) {
      body = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (v instanceof File) { body.append(k, v); return; }
        if (Array.isArray(v) || typeof v === "object") { body.append(k, JSON.stringify(v)); return; }
        body.append(k, String(v));
      });
    }
    const res = await api.put("/users/profile", body, {
      headers: hasFile ? { "Content-Type": "multipart/form-data" } : undefined,
    });
    return res.data?.data || res.data;
  },
  changePassword: async (payload) => {
    const res = await api.put("/users/profile-password", payload);
    return res.data;
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ROLE_META = {
  businessman: { label: "Business Owner", color: "bg-brand-500/20 text-white dark:text-white" },
  billing_staff: { label: "Billing Staff", color: "bg-sky-500/20 text-sky-600 dark:text-sky-400" },
  sales_staff: { label: "Sales Staff", color: "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" },
};

const STATUS_META = {
  active: { label: "Active", dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" },
  "in-active": { label: "Inactive", dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
  deleted: { label: "Deleted", dot: "bg-red-500", text: "text-red-600 dark:text-red-400" },
  blocked: { label: "Blocked", dot: "bg-rose-600", text: "text-rose-600 dark:text-rose-400" },
};

const formatDate = (v, withTime = false) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

const getInitials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "U";

const mapApiErrors = (err) => {
  const apiErrors = err?.response?.data?.errors;
  if (!Array.isArray(apiErrors)) return {};
  return apiErrors.reduce((acc, item) => {
    if (item?.path && item?.msg) acc[item.path] = item.msg;
    return acc;
  }, {});
};

// ─── Micro-components ─────────────────────────────────────────────────────────
const Toast = ({ toast }) =>
  toast.show ? (
    <div className={`fixed right-6 top-20 z-[80] flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-lg ${toast.type === "error"
      ? "border border-red-600 bg-red-500"
      : "border border-green-600 bg-green-500"
      }`}>
      {toast.type === "error"
        ? <IoClose className="h-4 w-4" />
        : <IoCheckmarkCircle className="h-4 w-4" />}
      {toast.message}
    </div>
  ) : null;

const FieldError = ({ msg }) =>
  msg ? <p className="mt-1 text-xs font-medium text-red-500">{msg}</p> : null;

const inputCls = (hasErr) =>
  `h-11 w-full rounded-xl border px-3 text-sm outline-none transition-colors dark:bg-navy-800 dark:text-white ${hasErr
    ? "border-red-400 text-red-600 placeholder:text-red-400 dark:border-red-400"
    : "border-gray-200 focus:border-brand-400 dark:border-white/10"
  }`;

const TABS = ["Personal", "Address", "Security", "Personal Notes"];

// ─── Profile Overview ─────────────────────────────────────────────────────────
const ProfileOverview = () => {
  const { user, setUser, setUserFromApi } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("Personal");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [imagePreview, setImagePreview] = useState("");
  const [newNote, setNewNote] = useState("");
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, index: null });
  const [showPassword, setShowPassword] = useState({ currentPassword: false, newPassword: false, confirmPassword: false });
  const [personal, setPersonal] = useState({ name: "", email: "", phone: "", profileImage: null, companyLogo: null, companySeal: null, companyName: "", companyShortName: "", gstNumber: "" });
  const [address, setAddress] = useState({ houseNameOrNumber: "", localityOrLandmark: "", pinCode: "", district: "", state: "", country: "" });
  const [security, setSecurity] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [openAddressDropdown, setOpenAddressDropdown] = useState(null);
  const [addressSearch, setAddressSearch] = useState({ country: "", state: "", district: "" });
  const [originalPersonal, setOriginalPersonal] = useState({ name: "", email: "", phone: "", companyName: "", companyShortName: "", gstNumber: "", companyLogo: null, companySeal: null });
  const [originalAddress, setOriginalAddress] = useState({ houseNameOrNumber: "", localityOrLandmark: "", pinCode: "", district: "", state: "", country: "" });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3500);
  };

  const handleTabChange = (tab) => {
    if (tab !== "Security") {
      setSecurity({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setShowPassword({ currentPassword: false, newPassword: false, confirmPassword: false });
    }
    if (tab !== "Personal Notes") {
      setNewNote("");
      setEditingNoteIndex(null);
      setEditNoteText("");
    }
    setActiveTab(tab);
    setFieldErrors({});
  };

  // Check if personal info has changes
  const hasPersonalChanges = () => {
    return (
      personal.name !== originalPersonal.name ||
      personal.email !== originalPersonal.email ||
      personal.phone !== originalPersonal.phone ||
      personal.companyName !== originalPersonal.companyName ||
      personal.companyShortName !== originalPersonal.companyShortName ||
      personal.gstNumber !== originalPersonal.gstNumber ||
      personal.profileImage instanceof File ||
      personal.companyLogo instanceof File ||
      personal.companySeal instanceof File
    );
  };

  // Check if address has changes
  const hasAddressChanges = () => {
    return (
      address.houseNameOrNumber !== originalAddress.houseNameOrNumber ||
      address.localityOrLandmark !== originalAddress.localityOrLandmark ||
      address.pinCode !== originalAddress.pinCode ||
      address.district !== originalAddress.district ||
      address.state !== originalAddress.state ||
      address.country !== originalAddress.country
    );
  };

  // Check if password fields are valid for submission
  const isPasswordValid = () => {
    return (
      security.currentPassword.trim() !== "" &&
      security.newPassword.trim() !== "" &&
      security.confirmPassword.trim() !== ""
    );
  };

  const countryOptions = useMemo(
    () => Country.getAllCountries().map((country) => country.name).sort(),
    [],
  );
  const selectedCountry = useMemo(
    () => Country.getAllCountries().find((country) => country.name === address.country),
    [address.country],
  );
  const stateOptions = useMemo(() => {
    if (!selectedCountry) return [];
    return State.getStatesOfCountry(selectedCountry.isoCode).map((state) => state.name).sort();
  }, [selectedCountry]);
  const selectedState = useMemo(() => {
    if (!selectedCountry || !address.state) return null;
    return State.getStatesOfCountry(selectedCountry.isoCode).find((state) => state.name === address.state);
  }, [selectedCountry, address.state]);
  const districtOptions = useMemo(() => {
    if (!selectedCountry || !selectedState) return [];
    return Array.from(new Set(City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map((city) => city.name))).sort();
  }, [selectedCountry, selectedState]);

  const getFilteredAddressOptions = (key, options) => {
    const term = (addressSearch[key] || "").trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.toLowerCase().includes(term));
  };

  const renderAddressDropdown = ({ key, label, value, options, disabled, placeholder, onSelect }) => {
    const filteredOptions = getFilteredAddressOptions(key, options);

    return (
      <div className="relative">
        <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">{label}</label>
        <button
          type="button"
          onClick={() => setOpenAddressDropdown((prev) => (prev === key ? null : key))}
          className={`flex h-11 w-full items-center justify-between rounded-xl border px-3 text-left text-sm dark:bg-navy-800 dark:text-white ${disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-white/10 dark:bg-gray-800/70 dark:text-gray-500"
            : "border-gray-200 bg-white hover:border-brand-400 dark:border-white/10 dark:bg-white/5"
            }`}
        >
          <span className="truncate">{value || placeholder}</span>
          <IoChevronDown className="h-4 w-4 text-gray-500" />
        </button>

        {openAddressDropdown === key && !disabled && (
          <div className="absolute top-full z-20 mt-2 w-full rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <input
              type="text"
              value={addressSearch[key] || ""}
              onChange={(e) => setAddressSearch((prev) => ({ ...prev, [key]: e.target.value }))}
              placeholder={`Search ${label.toLowerCase()}`}
              className="mb-2 h-9 w-full rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
            />
            <button
              type="button"
              onClick={() => {
                setAddressSearch((prev) => ({ ...prev, [key]: "" }));
                setOpenAddressDropdown(null);
              }}
              className="mb-2 h-8 w-full rounded-lg px-2 text-left text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Clear
            </button>
            <div className="max-h-44 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No options found</p>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      onSelect(option);
                      setOpenAddressDropdown(null);
                      setAddressSearch((prev) => ({ ...prev, [key]: "" }));
                    }}
                    className={`h-9 w-full rounded-lg px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${value === option ? "bg-brand-50 text-brand-600 dark:bg-brand-900/20 dark:text-brand-300" : "text-gray-700 dark:text-gray-200"}`}
                  >
                    {option}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await profileApi.get();
        setUserFromApi(data);
        setPersonal({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          profileImage: null,
          companyLogo: null,
          companyName: data.companyName || data.company_name || "",
          companyShortName: data.companyShortName || data.company_short_name || "",
          gstNumber: data.gstNumber || data.gst_number || "",
        });
        setOriginalPersonal({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          companyName: data.companyName || data.company_name || "",
          companyShortName: data.companyShortName || data.company_short_name || "",
          gstNumber: data.gstNumber || data.gst_number || "",
          companyLogo: data.companyLogo || null,
        });
        setAddress({
          houseNameOrNumber: data.houseNameOrNumber || "",
          localityOrLandmark: data.localityOrLandmark || "",
          pinCode: data.pinCode || "",
          district: data.district || "",
          state: data.state || "",
          country: data.country || "",
        });
        setOriginalAddress({
          houseNameOrNumber: data.houseNameOrNumber || "",
          localityOrLandmark: data.localityOrLandmark || "",
          pinCode: data.pinCode || "",
          district: data.district || "",
          state: data.state || "",
          country: data.country || "",
        });
        setImagePreview(data.profileImage || "");
      } catch (e) {
        setError(e?.response?.data?.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    })();
  }, [activeTab, setUser]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPersonal((p) => ({ ...p, profileImage: file }));
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result?.toString() || "");
    reader.readAsDataURL(file);
  };

  const handleCompanyLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPersonal((p) => ({ ...p, companyLogo: file }));
  };

  const handleCompanySealChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPersonal((p) => ({ ...p, companySeal: file }));
  };

  const savePersonal = async () => {
    setSaving(true); setFieldErrors({});
    const trimmedName = personal.name.trim();
    if (!trimmedName) {
      setFieldErrors({ name: "Name is required" });
      setSaving(false);
      return;
    }
    if (!/^[A-Za-z ]+$/.test(trimmedName)) {
      setFieldErrors({ name: "Name can only contain letters and spaces" });
      setSaving(false);
      return;
    }
    if (personal.companyShortName && personal.companyShortName.trim().length > 10) {
      setFieldErrors({ companyShortName: "Short name must be 10 characters or fewer" });
      setSaving(false);
      return;
    }

    try {
      const payload = {
        name: trimmedName,
        email: personal.email,
        phone: personal.phone,
        companyName: personal.companyName,
        companyShortName: personal.companyShortName,
        gstNumber: personal.gstNumber,
        ...(personal.profileImage instanceof File ? { profileImage: personal.profileImage } : {}),
        ...(personal.companyLogo instanceof File ? { companyLogo: personal.companyLogo } : {}),
        ...(personal.companySeal instanceof File ? { companySeal: personal.companySeal } : {}),
      };
      const updated = await profileApi.update(payload);
      setUserFromApi(updated);
      setPersonal((p) => ({
        ...p,
        name: updated.name ?? trimmedName,
        email: updated.email ?? p.email,
        phone: updated.phone ?? p.phone,
        companyName: updated.companyName ?? updated.company_name ?? p.companyName,
        companyShortName: updated.companyShortName ?? updated.company_short_name ?? p.companyShortName,
        gstNumber: updated.gstNumber ?? updated.gst_number ?? p.gstNumber,
        companyLogo: null,
        companySeal: null,
      }));
      setOriginalPersonal({
        name: updated.name ?? trimmedName,
        email: updated.email ?? personal.email,
        phone: updated.phone ?? personal.phone,
        companyName: updated.companyName ?? updated.company_name ?? personal.companyName,
        companyShortName: updated.companyShortName ?? updated.company_short_name ?? personal.companyShortName,
        gstNumber: updated.gstNumber ?? updated.gst_number ?? personal.gstNumber,
        companyLogo: updated.companyLogo ?? originalPersonal.companyLogo,
        companySeal: updated.companySeal ?? originalPersonal.companySeal,
      });
      setImagePreview(updated.profileImage || imagePreview);
      showToast("Personal info updated");
    } catch (e) {
      const fe = mapApiErrors(e);
      if (Object.keys(fe).length) setFieldErrors(fe);
      else showToast(e?.response?.data?.message || "Update failed", "error");
    } finally { setSaving(false); }
  };

  const saveAddress = async () => {
    setSaving(true); setFieldErrors({});
    try {
      const updated = await profileApi.update({ ...address });
      setUserFromApi(updated);
      setAddress((a) => ({
        ...a,
        houseNameOrNumber: updated.houseNameOrNumber ?? a.houseNameOrNumber,
        localityOrLandmark: updated.localityOrLandmark ?? a.localityOrLandmark,
        pinCode: updated.pinCode ?? a.pinCode,
        district: updated.district ?? a.district,
        state: updated.state ?? a.state,
        country: updated.country ?? a.country,
      }));
      setOriginalAddress({
        houseNameOrNumber: updated.houseNameOrNumber ?? address.houseNameOrNumber,
        localityOrLandmark: updated.localityOrLandmark ?? address.localityOrLandmark,
        pinCode: updated.pinCode ?? address.pinCode,
        district: updated.district ?? address.district,
        state: updated.state ?? address.state,
        country: updated.country ?? address.country,
      });
      showToast("Address updated");
    } catch (e) {
      const fe = mapApiErrors(e);
      if (Object.keys(fe).length) setFieldErrors(fe);
      else showToast(e?.response?.data?.message || "Update failed", "error");
    } finally { setSaving(false); }
  };

  const savePassword = async () => {
    setFieldErrors({});
    if (security.newPassword !== security.confirmPassword) {
      setFieldErrors({ confirmPassword: "Passwords do not match" }); return;
    }
    if (security.newPassword.length < 8) {
      setFieldErrors({ newPassword: "Must be at least 8 characters" }); return;
    }
    setSaving(true);
    try {
      await profileApi.changePassword({ currentPassword: security.currentPassword, newPassword: security.newPassword, confirmNewPassword: security.confirmPassword });
      setSecurity({ currentPassword: "", newPassword: "", confirmPassword: "" });
      showToast("Password changed successfully");
    } catch (e) {
      const fe = mapApiErrors(e);
      if (Object.keys(fe).length) setFieldErrors(fe);
      else showToast(e?.response?.data?.message || "Password change failed", "error");
    } finally { setSaving(false); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const nextNotes = [
        ...(Array.isArray(user?.notes) ? user.notes : []),
        { date: new Date().toISOString(), note: newNote.trim() },
      ];
      const updated = await profileApi.update({ notes: nextNotes });
      setUser((u) => ({ ...u, notes: updated.notes || nextNotes }));
      setNewNote("");
      showToast("Note added");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to add note", "error");
    } finally { setSaving(false); }
  };

  const handleEditNote = (index) => {
    setEditingNoteIndex(index);
    setEditNoteText((user?.notes || [])[index]?.note || "");
  };

  const handleSaveNote = async () => {
    if (!editNoteText.trim()) {
      showToast("Note cannot be empty", "error");
      return;
    }
    setSaving(true);
    try {
      const nextNotes = (user?.notes || []).map((note, i) =>
        i === editingNoteIndex ? { ...note, note: editNoteText.trim() } : note
      );
      const updated = await profileApi.update({ notes: nextNotes });
      setUser((u) => ({ ...u, notes: updated.notes || nextNotes }));
      setEditingNoteIndex(null);
      setEditNoteText("");
      showToast("Note updated");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to update note", "error");
    } finally { setSaving(false); }
  };

  const handleCancelEdit = () => {
    setEditingNoteIndex(null);
    setEditNoteText("");
  };

  const handleDeleteNote = async (index) => {
    setSaving(true);
    try {
      const nextNotes = (user?.notes || []).filter((_, i) => i !== index);
      const updated = await profileApi.update({ notes: nextNotes });
      setUser((u) => ({ ...u, notes: updated.notes || nextNotes }));
      showToast("Note deleted");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to delete note", "error");
    } finally { setSaving(false); setDeleteConfirm({ show: false, index: null }); }
  };

  const roleMeta = ROLE_META[user?.role] || { label: user?.role || "—", color: "bg-gray-100 text-gray-600" };
  const statusMeta = STATUS_META[user?.status] || { label: user?.status || "—", dot: "bg-gray-400", text: "text-gray-600" };
  const fullAddress = [
    user?.houseNameOrNumber, user?.localityOrLandmark,
    user?.district, user?.state, user?.country, user?.pinCode,
  ].filter(Boolean).join(", ") || "—";

  // ── Loading ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-[70vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-300">Loading your profile…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex h-[70vh] items-center justify-center">
        <div className="rounded-2xl border border-red-200 bg-red-50 px-8 py-6 text-center dark:border-red-500/20 dark:bg-red-900/20">
          <p className="text-base font-semibold text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  // ── Main ─────────────────────────────────────────────
  return (
    <div className="flex w-full flex-col gap-5 pb-6">
      <Toast toast={toast} />

      {/* ── Hero strip ───────────────────────────────── */}
      <div className="relative mt-3 overflow-hidden rounded-3xl bg-gradient-to-br from-brand-500 via-brand-400 to-sky-300 px-6 pb-8 pt-8 shadow-xl shadow-brand-400/20">
        <div className="pointer-events-none absolute -top-12 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-14 right-16 h-44 w-44 rounded-full bg-sky-400/20 blur-2xl" />

        <div className="relative flex flex-col items-center gap-5 sm:flex-row sm:items-end">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border-4 border-white/40 bg-white/20 text-white shadow-lg">
              {imagePreview
                ? <img src={imagePreview} alt="avatar" className="h-full w-full object-cover" />
                : <span className="text-3xl font-extrabold">{getInitials(user?.name)}</span>}
            </div>
          </div>

          {/* Identity chips */}
          <div className="text-center sm:text-left">
            <h2 className="text-2xl font-extrabold tracking-tight text-white drop-shadow">{user?.name || "—"}</h2>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              <span className={`rounded-lg px-3 py-1 text-xs font-bold ${roleMeta.color}`}>{roleMeta.label}</span>
              <span className={`flex items-center gap-1.5 rounded-lg bg-white/20 px-3 py-1 text-xs font-bold ${statusMeta.text}`}>
                <span className={`h-2 w-2 rounded-full ${statusMeta.dot}`} />
                {statusMeta.label}
              </span>
              <span className="rounded-lg bg-white/20 px-3 py-1 text-xs font-medium text-white/90">
                Since {formatDate(user?.createdAt)}
              </span>
            </div>
          </div>

          {/* Quick contact (right edge, large screens) */}
          <div className="ml-auto hidden flex-col gap-2 lg:flex">
            {user?.phone && (
              <div className="flex items-center gap-2 rounded-xl bg-white/15 px-3 py-1.5 text-sm text-white backdrop-blur">
                <IoCall className="h-4 w-4 text-white/70" /> {user.phone}
              </div>
            )}
            {user?.email && (
              <div className="flex items-center gap-2 rounded-xl bg-white/15 px-3 py-1.5 text-sm text-white backdrop-blur">
                <IoMail className="h-4 w-4 text-white/70" /> {user.email}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main grid ────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-start">

        {/* Left: identity sidebar */}
        <div className="flex flex-col gap-5 lg:col-span-4">
          <Card extra="p-5">
            <p className="mb-4 text-xs font-bold uppercase tracking-widest text-brand-500">Account Overview</p>
            <div className="space-y-3">
              {[
                { icon: <IoPerson />, label: "Full Name", value: user?.name },
                { icon: <IoMail />, label: "Email", value: user?.email },
                { icon: <IoCall />, label: "Phone", value: user?.phone },
                { icon: <IoBusiness />, label: "Company Name", value: user?.companyName },
                { icon: <IoBusiness />, label: "Company Short Name", value: user?.companyShortName },
                { icon: <IoShield />, label: "Role", value: roleMeta.label },
                { icon: <IoLocationSharp />, label: "Address", value: fullAddress },
                { icon: <IoTime />, label: "Member Since", value: formatDate(user?.createdAt) },
                { icon: <IoTime />, label: "Last Modified", value: formatDate(user?.modifiedAt) },
              ].map(({ icon, label, value }) => (
                <div key={label} className="flex items-start gap-3 rounded-xl bg-gray-50 px-3 py-3 dark:bg-white/5">
                  <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-brand-500/15 text-sm text-brand-500">
                    {icon}
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                    <p className="mt-0.5 break-all text-sm font-semibold text-navy-700 dark:text-white">{value || "—"}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card extra="p-5">
            <p className="mb-4 text-xs font-bold uppercase tracking-widest text-brand-500">Notes</p>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-500/15 text-2xl font-extrabold text-brand-500">
                {user?.notes?.length ?? 0}
              </div>
              <div>
                <p className="text-base font-bold text-navy-700 dark:text-white">Total Notes</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Across this account</p>
              </div>
            </div>
            <button
              onClick={() => handleTabChange("Personal Notes")}
              className="mt-4 w-full rounded-xl border border-brand-400/30 bg-brand-500/10 py-2 text-xs font-bold text-brand-600 hover:bg-brand-500/20 dark:text-brand-400"
            >
              View all notes →
            </button>
          </Card>
        </div>

        {/* Right: tabbed editor */}
        <Card extra="col-span-1 flex flex-col overflow-hidden p-0 lg:col-span-8">
          {/* Tab bar */}
          <div className="flex border-b border-gray-100 dark:border-white/10">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`flex-1 py-3.5 text-sm font-semibold transition-colors sm:flex-none sm:px-6 ${activeTab === tab
                  ? "border-b-2 border-brand-500 text-brand-600 dark:text-brand-400"
                  : "text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white"
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-6">

            {/* ── Personal ── */}
            {activeTab === "Personal" && (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50/70 p-4 dark:border-white/10 dark:bg-white/5">
                    <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-white bg-brand-500/20 text-brand-500 shadow">
                      {imagePreview
                        ? <img src={imagePreview} alt="preview" className="h-full w-full object-cover" />
                        : <span className="text-xl font-extrabold">{getInitials(personal.name)}</span>}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-navy-700 dark:text-white">Profile Photo</p>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">JPG, JPEG or PNG</p>
                      <label className="mt-2 inline-flex cursor-pointer rounded-lg bg-brand-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-600">
                        Choose File
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="sr-only"
                        />
                      </label>
                      <FieldError msg={fieldErrors.profileImage} />
                    </div>
                  </div>

                  <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50/70 p-4 dark:border-white/10 dark:bg-white/5">
                    <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-white bg-brand-500/20 text-brand-500 shadow">
                      {personal.companyLogo instanceof File
                        ? <img src={URL.createObjectURL(personal.companyLogo)} alt="company logo preview" className="h-full w-full object-contain p-1" />
                        : user?.companyLogo
                          ? <img src={user.companyLogo.startsWith('http') ? user.companyLogo : `${API_BASE_URL}${user.companyLogo}`} alt="company logo" className="h-full w-full object-contain p-1" />
                          : <span className="text-xl font-extrabold">{getInitials(personal.companyName || "Co")}</span>}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-navy-700 dark:text-white">Company Logo</p>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Used for Receipts</p>
                      <label className="mt-2 inline-flex cursor-pointer rounded-lg bg-brand-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-600">
                        Choose File
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCompanyLogoChange}
                          className="sr-only"
                        />
                      </label>
                      <FieldError msg={fieldErrors.companyLogo} />
                    </div>
                  </div>

                  {/* Company Seal */}
                  <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50/70 p-4 dark:border-white/10 dark:bg-white/5">
                    <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-white bg-brand-500/20 text-brand-500 shadow">
                      {personal.companySeal instanceof File
                        ? <img src={URL.createObjectURL(personal.companySeal)} alt="company seal preview" className="h-full w-full object-contain p-1" />
                        : user?.companySeal
                          ? <img src={user.companySeal.startsWith('http') ? user.companySeal : `${API_BASE_URL}${user.companySeal}`} alt="company seal" className="h-full w-full object-contain p-1" />
                          : <span className="text-xl font-extrabold text-brand-500">Seal</span>}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-navy-700 dark:text-white">Company Seal</p>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Used in Receipt Footer</p>
                      <label className="mt-2 inline-flex cursor-pointer rounded-lg bg-brand-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-600">
                        Choose File
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCompanySealChange}
                          className="sr-only"
                        />
                      </label>
                      <FieldError msg={fieldErrors.companySeal} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">Full Name*</label>
                    <input value={personal.name} onChange={(e) => setPersonal((p) => ({ ...p, name: e.target.value.replace(/[^A-Za-z ]/g, "") }))} placeholder="Full name" className={inputCls(fieldErrors.name)} />
                    <FieldError msg={fieldErrors.name} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">Company Name</label>
                    <input value={personal.companyName} onChange={(e) => setPersonal((p) => ({ ...p, companyName: e.target.value }))} placeholder="Company name" className={inputCls(fieldErrors.companyName)} />
                    <FieldError msg={fieldErrors.companyName} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">Company Short Name</label>
                    <input value={personal.companyShortName} onChange={(e) => setPersonal((p) => ({ ...p, companyShortName: e.target.value.slice(0, 10) }))} placeholder="Short name (max 10 chars)" className={inputCls(fieldErrors.companyShortName)} />
                    <FieldError msg={fieldErrors.companyShortName} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">GST Number</label>
                    <input value={personal.gstNumber} onChange={(e) => setPersonal((p) => ({ ...p, gstNumber: e.target.value.toUpperCase() }))} placeholder="GST Number" className={inputCls(fieldErrors.gstNumber)} />
                    <FieldError msg={fieldErrors.gstNumber} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">Phone*</label>
                    <input value={personal.phone} onChange={(e) => setPersonal((p) => ({ ...p, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} placeholder="10 digit mobile" className={inputCls(fieldErrors.phone)} />
                    <FieldError msg={fieldErrors.phone} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">Email*</label>
                    <input type="email" value={personal.email} onChange={(e) => setPersonal((p) => ({ ...p, email: e.target.value }))} placeholder="name@example.com" className={inputCls(fieldErrors.email)} />
                    <FieldError msg={fieldErrors.email} />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button onClick={savePersonal} disabled={saving || !hasPersonalChanges()} className="rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm shadow-brand-500/30 hover:from-brand-600 hover:to-brand-700 disabled:opacity-50">
                    {saving ? "Saving…" : "Save Personal Info"}
                  </button>
                </div>
              </div>
            )}

            {/* ── Address ── */}
            {activeTab === "Address" && (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">House Name / Number</label>
                    <input
                      value={address.houseNameOrNumber}
                      onChange={(e) => setAddress((a) => ({ ...a, houseNameOrNumber: e.target.value.replace(/[^A-Za-z0-9/\- ]/g, "") }))}
                      placeholder="e.g. Sunrise Apartments"
                      className={inputCls(fieldErrors.houseNameOrNumber)}
                    />
                    <FieldError msg={fieldErrors.houseNameOrNumber} />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">Locality / Landmark</label>
                    <input
                      value={address.localityOrLandmark}
                      onChange={(e) => setAddress((a) => ({ ...a, localityOrLandmark: e.target.value.replace(/[^A-Za-z ]/g, "") }))}
                      placeholder="e.g. Near City Mall"
                      className={inputCls(fieldErrors.localityOrLandmark)}
                    />
                    <FieldError msg={fieldErrors.localityOrLandmark} />
                  </div>

                  <div>
                    {renderAddressDropdown({
                      key: "country",
                      label: "Country",
                      value: address.country,
                      placeholder: "Select country",
                      options: countryOptions,
                      onSelect: (value) => setAddress((a) => ({ ...a, country: value, state: "", district: "" })),
                    })}
                    <FieldError msg={fieldErrors.country} />
                  </div>

                  <div>
                    {renderAddressDropdown({
                      key: "state",
                      label: "State",
                      value: address.state,
                      placeholder: "Select state",
                      options: stateOptions,
                      disabled: !address.country,
                      onSelect: (value) => setAddress((a) => ({ ...a, state: value, district: "" })),
                    })}
                    <FieldError msg={fieldErrors.state} />
                  </div>

                  <div>
                    {renderAddressDropdown({
                      key: "district",
                      label: "District",
                      value: address.district,
                      placeholder: "Select district",
                      options: districtOptions,
                      disabled: !address.state,
                      onSelect: (value) => setAddress((a) => ({ ...a, district: value })),
                    })}
                    <FieldError msg={fieldErrors.district} />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">Pin Code</label>
                    <input
                      value={address.pinCode}
                      onChange={(e) => setAddress((a) => ({ ...a, pinCode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                      placeholder="6-digit PIN"
                      maxLength={6}
                      className={inputCls(fieldErrors.pinCode)}
                    />
                    <FieldError msg={fieldErrors.pinCode} />
                  </div>
                </div>

                {Object.values(address).some(Boolean) && (
                  <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:bg-white/5 dark:text-gray-300">
                    <IoLocationSharp className="h-4 w-4 flex-shrink-0 text-brand-500" />
                    {[address.houseNameOrNumber, address.localityOrLandmark, address.district, address.state, address.country, address.pinCode].filter(Boolean).join(", ")}
                  </div>
                )}

                <div className="flex justify-end">
                  <button onClick={saveAddress} disabled={saving || !hasAddressChanges()} className="rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm shadow-brand-500/30 hover:from-brand-600 hover:to-brand-700 disabled:opacity-50">
                    {saving ? "Saving…" : "Save Address"}
                  </button>
                </div>
              </div>
            )}

            {/* ── Security ── */}
            {activeTab === "Security" && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 dark:border-amber-500/20 dark:bg-amber-900/10">
                  <IoLockClosed className="h-5 w-5 flex-shrink-0 text-amber-500" />
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    Use a strong password of at least 8 characters. Never share it with anyone.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {[
                    { key: "currentPassword", label: "Current Password", placeholder: "Enter current password" },
                    { key: "newPassword", label: "New Password", placeholder: "Min. 8 characters" },
                    { key: "confirmPassword", label: "Confirm New Password", placeholder: "Repeat new password" },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label className="mb-1 block text-xs font-bold text-navy-700 dark:text-white">{label}</label>
                      <div className="relative">
                        <input
                          type={showPassword[key] ? "text" : "password"}
                          value={security[key]}
                          onChange={(e) => setSecurity((s) => ({ ...s, [key]: e.target.value }))}
                          placeholder={placeholder}
                          className={inputCls(fieldErrors[key])}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => ({ ...s, [key]: !s[key] }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                        >
                          {showPassword[key] ? <IoEyeOff className="h-5 w-5" /> : <IoEye className="h-5 w-5" />}
                        </button>
                      </div>
                      <FieldError msg={fieldErrors[key]} />
                    </div>
                  ))}
                </div>

                <div className="flex justify-end">
                  <button onClick={savePassword} disabled={saving || !isPasswordValid()} className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm shadow-amber-500/30 hover:from-amber-600 hover:to-amber-700 disabled:opacity-50">
                    {saving ? "Saving…" : "Change Password"}
                  </button>
                </div>
              </div>
            )}

            {/* ── Notes ── */}
            {activeTab === "Personal Notes" && (
              <div className="flex flex-col gap-4">
                {editingNoteIndex === null ? (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={2}
                      maxLength={1000}
                      placeholder="Write a note about this account…"
                      className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                    />
                    <button
                      onClick={handleAddNote}
                      disabled={saving || !newNote.trim()}
                      className="min-w-[110px] self-end rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-2.5 text-sm font-bold text-white hover:from-brand-600 hover:to-brand-700 disabled:opacity-50"
                    >
                      Add Note
                    </button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-700/50">
                    <div className="mb-3 flex items-center justify-between">
                      <h5 className="text-sm font-semibold text-navy-700 dark:text-white">Edit Note</h5>
                      <button
                        onClick={handleCancelEdit}
                        className="rounded-full p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <IoClose className="h-4 w-4" />
                      </button>
                    </div>

                    <textarea
                      value={editNoteText}
                      onChange={(e) => setEditNoteText(e.target.value)}
                      rows={2}
                      maxLength={1000}
                      placeholder="Edit your note here…"
                      className="mb-3 min-h-[100px] w-full resize-none rounded-lg border border-gray-200 bg-white p-3 text-sm text-navy-700 placeholder-gray-400 outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder-gray-500"
                    />

                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveNote}
                        disabled={saving}
                        className="flex items-center gap-1 flex-1 justify-center rounded-lg bg-green-500 px-3 py-2 text-sm font-medium text-white hover:bg-green-600 transition-colors disabled:opacity-50"
                      >
                        <MdSave className="h-4 w-4" />
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={saving}
                        className="flex items-center gap-1 flex-1 justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50"
                      >
                        <MdCancel className="h-4 w-4" />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {Array.isArray(user?.notes) && user.notes.length > 0 ? (
                  <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
                    {[...user.notes].reverse().map((item, ri) => {
                      const origIndex = user.notes.length - 1 - ri;
                      return (
                        <div key={`${item.date}-${ri}`} className="group flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-white/10 dark:bg-white/5">
                          <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-brand-500" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(item.date, true)}</p>
                            <p className="mt-1 text-sm text-navy-700 dark:text-white">{item.note}</p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              onClick={() => handleEditNote(origIndex)}
                              disabled={saving}
                              className="rounded-lg p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-500 transition-colors dark:hover:bg-blue-900/20 dark:hover:text-blue-400 disabled:opacity-50"
                              title="Edit"
                            >
                              <MdEdit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm({ show: true, index: origIndex })}
                              disabled={saving}
                              className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors dark:hover:bg-red-900/20 dark:hover:text-red-400 disabled:opacity-50"
                              title="Delete"
                            >
                              <IoClose className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 py-6 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-white/5">
                      <IoPerson className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">No notes yet. Add the first one above.</p>
                  </div>
                )}
              </div>
            )}

          </div>
        </Card>
      </div>

      {/* ── Delete note confirm ───────────────────────── */}
      {deleteConfirm.show && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 p-4 backdrop-blur-sm"
          onClick={() => !saving && setDeleteConfirm({ show: false, index: null })}
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-navy-700 dark:text-white">Delete this note?</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">This action is permanent and cannot be undone.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                disabled={saving}
                onClick={() => setDeleteConfirm({ show: false, index: null })}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:border-white/10 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                disabled={saving}
                onClick={() => handleDeleteNote(deleteConfirm.index)}
                className="rounded-xl bg-red-500 px-4 py-2 text-sm font-bold text-white hover:bg-red-600 disabled:opacity-50"
              >
                {saving ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileOverview;
